// src/pos-app/shell/StationTopBar.js
//
// Shared station top bar (F12a Task 6.2) — the glass chrome header every
// station screen (Cashier / Waiter / KDS) mounts. Matches the approved
// mockups (docs/mockups/f12a/*.html).
//
//   <StationTopBar
//     stationLabel="Cashier · Till 1"   station chip text (caller translates)
//     operator={{ name: 'Sara M.' }}    optional — chip renders only if given
//     onLock={fn}                       optional — Lock button only if given
//     onSettings={fn}                   optional — gear button only if given
//     compact                           optional — see below, default false
//   >{center content}</StationTopBar>
//
// KDS passes no operator/onLock; the props drive which elements show.
// Tenant name comes from useAuth().getBrandName(). Safe-area aware
// (--safe-top) and RTL-safe (logical properties / flex only).
//
// `compact` (Task N — user-QC finding 1, additive prop, default false): the
// native cashier surface's TopBarExtras children cluster (session pill +
// connection chip + More menu) made the bar crowded enough at iPad-portrait
// widths (~1024px) that the station chip risked losing legibility. The
// station chip (`.stb-chip`) itself was already `flex:none` (never shrinks);
// the fix instead frees width by hiding the tenant brand name below 1024px.
// That rule lives ENTIRELY under the `.stb-compact` class so it is provably
// impossible — by CSS selector, not just convention — for it to change
// Waiter/KDS, which never pass `compact` and so never get the class.
// CashierStation passes `compact={nativeSell}` (native Sell surface only);
// legacy Cashier mode and every other station render the pre-existing CSS
// unchanged.
import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
// No glass() import: this bar uses the shared .aq-liquid class instead, which
// AuroraBackground injects app-wide.

const CSS = `
/* Full-bleed, like the bottom tab bar — the two are a matched pair of chrome
   and both now meet the display edges rather than floating as inset cards.
   Owns its own horizontal padding because the station frames render it
   outside their padded content column. */
.stb-bar{display:flex;align-items:center;gap:12px;flex:none;box-sizing:border-box;
  width:100%;padding:calc(8px + var(--safe-top)) 16px 10px;min-height:60px;
  border-bottom:1px solid color-mix(in srgb, var(--c-border) 45%, transparent);
  box-shadow:0 8px 26px color-mix(in srgb, var(--c-ink) 6%, transparent);}
/* The specular rim belongs on the BOTTOM edge here — this bar's lit edge is
   the one facing the content it floats over. */
.stb-bar.aq-liquid::before{top:auto;bottom:0;}
.stb-left{display:flex;align-items:center;gap:11px;min-width:0;}
/* No tile, no gradient — the mark sits directly on the bar, which is how the
   brand is presented everywhere else (see the web portal's PortalLayout and
   the landing header). The coloured rounded square was invented by the POS
   shell and did not match. */
.stb-brandmark{width:38px;height:38px;flex:none;
  display:flex;align-items:center;justify-content:center;}
/* The mark is dark ink on light surfaces and the white variant on dark ones,
   swapped by the theme attribute — a dark mark on a dark bar would vanish.
   object-fit keeps it undistorted at any size. */
.stb-brandmark img{width:100%;height:100%;object-fit:contain;display:block;}
:root[data-theme="dark"] .stb-brandmark img.mark-dark{display:none;}
.stb-brandmark img.mark-light{display:none;}
:root[data-theme="dark"] .stb-brandmark img.mark-light{display:block;}
.stb-tenant{font-size:16px;font-weight:800;letter-spacing:-.2px;color:var(--c-ink);
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.stb-chip{display:inline-flex;align-items:center;gap:6px;flex:none;
  padding:6px 12px;border-radius:999px;font-size:12px;font-weight:700;
  background:var(--c-greenLight);color:var(--c-green);border:1px solid var(--c-greenBorder);
  white-space:nowrap;}
.stb-center{flex:1;min-width:0;display:flex;align-items:center;justify-content:center;gap:8px;}
.stb-spacer{flex:1;}
.stb-right{display:flex;align-items:center;gap:10px;flex:none;}
.stb-operator{display:flex;align-items:center;gap:9px;min-height:44px;box-sizing:border-box;
  padding:5px;padding-inline-end:14px;border-radius:999px;
  background:var(--c-surface);border:1px solid var(--c-border);}
.stb-avatar{width:34px;height:34px;border-radius:50%;flex:none;
  display:flex;align-items:center;justify-content:center;color:#fff;
  background:linear-gradient(135deg,var(--c-green),var(--c-teal));
  font-size:12px;font-weight:800;letter-spacing:.3px;}
.stb-opname{font-size:13px;font-weight:700;color:var(--c-ink);white-space:nowrap;}
.stb-lock{display:inline-flex;align-items:center;gap:8px;flex:none;
  min-height:44px;padding:0 18px;border:none;border-radius:12px;cursor:pointer;
  background:var(--c-ink);color:var(--c-bg);
  font-size:13.5px;font-weight:800;font-family:inherit;
  transition:transform .15s ease, filter .15s ease;}
.stb-lock:active{transform:scale(.96);filter:brightness(1.1);}
.stb-gear{width:44px;height:44px;border-radius:12px;flex:none;cursor:pointer;
  display:flex;align-items:center;justify-content:center;
  background:var(--c-surface);border:1px solid var(--c-border);color:var(--c-slate);
  transition:transform .15s ease, background .15s ease;}
.stb-gear:active{transform:scale(.94);background:var(--c-faint);}
@media (max-width:560px){
  .stb-tenant{font-size:14px;}
  .stb-lock{padding:0 14px;}
}
/* Phone width (F15 follow-up). The bar is a flex row of fixed-width items —
   38px brandmark + session chip + 34px avatar + Lock + 44px gear, plus gaps —
   and .stb-right is flex:none, so at ~393px nothing yields and the children
   overlap and clip. Captured on a real render at 1320x2868: the tenant name,
   session chip, "Online" chip, operator name, Lock and gear all collided.

   Drop what a cashier does not need on a phone and keep every ACTION:
   the session id and operator name are reference detail (both remain one tap
   away in the settings sheet), the avatar still identifies who is signed in,
   and Lock and the gear stay because they are the only way to lock the
   station or reconfigure the device. */
@media (max-width:768px){
  .stb-tenant{display:none;}
  .stb-chip{display:none;}
  .stb-opname{display:none;}
  .stb-center{display:none;}
  .stb-bar{gap:8px;}
  .stb-operator{gap:0;padding-inline:0;}
}
/* compact (Task N finding 1) — scoped under .stb-compact so it can only ever
   apply to callers that opt in (CashierStation's native surface); Waiter/KDS
   never render this class, so this rule can never match their markup. */
@media (max-width:1024px){
  .stb-compact .stb-tenant{display:none;}
}
`;

const LockIcon = (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="4" y="11" width="16" height="10" rx="2" />
        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
);

const GearIcon = (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
);

const StationIcon = (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="4" width="18" height="14" rx="2" />
        <path d="M8 21h8M12 18v3" />
    </svg>
);

// Real brand asset, not a hand-rolled glyph. Served from public/ — CRA copies
// public/ verbatim into build/, and scripts/prune-native-build.js only strips
// downloads/, help-screenshots/ and *.map, so root-level PNGs reach the
// Capacitor bundle intact.
// Both variants render; CSS shows whichever suits the active theme. Only the
// visible one carries the alt text so a screen reader announces the brand once.
const AqanaMark = (
    <>
        <img className="mark-dark" src="/aqana-mark.png" alt="Aqana" />
        <img className="mark-light" src="/aqana-mark-light.png" alt="" aria-hidden="true" />
    </>
);

function initials(name) {
    return String(name || '')
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((word) => word.charAt(0))
        .join('')
        .toUpperCase();
}

export default function StationTopBar({ stationLabel, operator, onLock, onSettings, compact = false, children }) {
    const { getBrandName } = useAuth();
    const { t } = useLanguage();

    const tenantName = (typeof getBrandName === 'function' && getBrandName()) || '';

    // Liquid Glass, the same material as the bottom tab bar — the two are a
    // matched pair of chrome and read as inconsistent when the top bar was a
    // glass() card and the tab bar was not.
    return (
        <header className={`stb-bar aq-liquid${compact ? ' stb-compact' : ''}`}>
            <style>{CSS}</style>
            <div className="stb-left">
                <div className="stb-brandmark">{AqanaMark}</div>
                {tenantName ? <span className="stb-tenant">{tenantName}</span> : null}
                {stationLabel ? (
                    <span className="stb-chip">
                        {StationIcon}
                        {stationLabel}
                    </span>
                ) : null}
            </div>
            {children ? <div className="stb-center">{children}</div> : <div className="stb-spacer" />}
            <div className="stb-right">
                {operator ? (
                    <div className="stb-operator">
                        <span className="stb-avatar" aria-hidden="true">{initials(operator.name)}</span>
                        <span className="stb-opname">{operator.name}</span>
                    </div>
                ) : null}
                {onLock ? (
                    <button type="button" className="stb-lock" onClick={onLock}>
                        {LockIcon}
                        {t('lock')}
                    </button>
                ) : null}
                {onSettings ? (
                    <button type="button" className="stb-gear" onClick={onSettings} aria-label={t('station_settings')}>
                        {GearIcon}
                    </button>
                ) : null}
            </div>
        </header>
    );
}
